import {
  FC,
  ReactNode,
  useState,
  useCallback,
  MouseEventHandler,
  cloneElement,
  ReactElement,
  CSSProperties,
} from 'react';
import { Placement } from '@floating-ui/react';
import { paddingSM } from '@atlas-frontend/design-tokens';
import { Tooltip } from './Tooltip';

interface PopoverPropsBase {
  content: ReactNode;
  placement?: Placement | 'auto';
  wrapperType?: 'span' | 'div';
  style?: CSSProperties;
  stopClickPropagation?: boolean;
  children: ReactElement<{ onClick?: MouseEventHandler<HTMLElement> }>;
}

export type PopoverProps = PopoverPropsBase &
  (
    | {
        isOpen: boolean;
        onOpenChange: (isOpen: boolean) => void;
      }
    | {
        isOpen?: never;
        onOpenChange?: never;
      }
  );

export const Popover: FC<PopoverProps> = (props) => {
  const {
    content,
    children,
    wrapperType,
    style,
    placement,
    stopClickPropagation,
    ...rest
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const isControlled = typeof rest.isOpen === 'boolean';

  const toggleClick: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      setIsOpen((current) => !current);
      children.props.onClick?.(event);
    },
    [children],
  );

  return (
    <Tooltip
      trigger="click"
      variant="light"
      wrapperType={wrapperType}
      placement={placement}
      title={content}
      stopClickPropagation={stopClickPropagation}
      style={{ padding: paddingSM, ...style }}
      {...(isControlled ? rest : { isOpen, onOpenChange: setIsOpen })}
    >
      {isControlled
        ? children
        : cloneElement(children, { onClick: toggleClick })}
    </Tooltip>
  );
};
